import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import UserAvatar from './UserAvatar';
import AvatarStatusBadge from './AvatarStatusBadge';

/**
 * The card you get by clicking someone's avatar in chat.
 *
 * Chat showed an avatar and a display name and nothing else — there was no way
 * to find out who you were actually talking to without leaving the conversation.
 * This is that: a portrait, the name, the email, in a popover anchored to the
 * avatar you clicked.
 *
 * Portalled to <body> and positioned fixed, matching ChannelMembersPopover: the
 * message list is a scroll container with its own stacking context, so a card
 * rendered inline would clip at the viewport edge and scroll away from its own
 * anchor.
 *
 * `actions` is a deliberate slot, empty today. The owner asked for room to add
 * "add" / "follow" later, and reserving it now means the layout does not have
 * to be redesigned around a button that arrives afterwards. It renders nothing
 * when nothing is passed — no empty bordered strip.
 */

// Wider than the original 248: an email is the longest thing on the card and
// was ellipsizing on ordinary addresses.
const CARD_WIDTH = 288;
const GAP = 8;

export default function UserProfileCard({
  userId,
  name,
  email,
  anchorRect,
  onClose,
  actions,
  // Hover-opened callers (the board card's assignee avatar) need to know when
  // the pointer is over the CARD, not just over the anchor — the card is
  // portalled to <body>, so it is nowhere near the avatar in the DOM and the
  // anchor's own mouseleave fires the moment you move towards it.
  onMouseEnter,
  onMouseLeave,
}) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Measured, not guessed: the card's height depends on whether an email is
  // present and on how the name wraps, and a fixed estimate flips it to the
  // wrong side of a message near the viewport edge.
  useLayoutEffect(() => {
    if (!anchorRect) return;
    const h = cardRef.current?.offsetHeight ?? 120;
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const top =
      spaceBelow >= h + GAP
        ? anchorRect.bottom + GAP
        : Math.max(GAP, anchorRect.top - h - GAP);
    const left = Math.min(
      Math.max(GAP, anchorRect.left),
      Math.max(GAP, window.innerWidth - CARD_WIDTH - GAP)
    );
    setPos({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onDown = (e) => {
      if (!cardRef.current?.contains(e.target)) onClose?.();
    };
    // `true` — capture. The message row has its own mousedown handlers, and a
    // bubbling listener would let a click inside the list act on the row behind
    // the card before the card closed.
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    // A scroll moves the anchor out from under a fixed card, so close rather
    // than chase it.
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  return createPortal(
    <div
      ref={cardRef}
      className="user-profile-card"
      role="dialog"
      aria-label={name || 'Profile'}
      style={{
        position: 'fixed',
        width: CARD_WIDTH,
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        // Hidden until measured, so it never paints at the wrong spot first.
        visibility: pos ? 'visible' : 'hidden',
        zIndex: 10050,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="user-profile-card-head">
        <UserAvatar userId={userId} name={name} size={56}>
          <AvatarStatusBadge userId={userId} avatarSize={56} />
        </UserAvatar>
        <div className="user-profile-card-texts">
          <span className="user-profile-card-name">{name || 'Unknown'}</span>
          {email ? (
            <span className="user-profile-card-email" title={email}>
              {email}
            </span>
          ) : (
            // Says "not available" rather than rendering an empty line, so a
            // missing email reads as missing data instead of a broken card.
            <span className="user-profile-card-email user-profile-card-email--none">
              No email available
            </span>
          )}
        </div>
      </div>
      {actions ? <div className="user-profile-card-actions">{actions}</div> : null}
    </div>,
    document.body
  );
}
